// Analytics integration for Web Vitals
// Sends metrics to analytics endpoint for production monitoring

interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

interface AnalyticsPayload {
  metric: WebVitalMetric;
  url: string;
  userAgent: string;
  timestamp: number;
  connection?: string;
  deviceMemory?: number;
}

// Queue for batching metrics
let metricsQueue: AnalyticsPayload[] = [];
let flushTimeout: NodeJS.Timeout | null = null;

// Flush interval (send batch every 5 seconds)
const FLUSH_INTERVAL = 5000;
const MAX_QUEUE_SIZE = 10;

// Get connection info
const getConnectionInfo = (): string | undefined => {
  const nav = navigator as any;
  if (nav.connection) {
    return nav.connection.effectiveType;
  }
  return undefined;
};

// Get device memory
const getDeviceMemory = (): number | undefined => {
  const nav = navigator as any;
  return nav.deviceMemory;
};

// Send metrics to analytics
const sendToAnalytics = async (metrics: AnalyticsPayload[]) => {
  if (metrics.length === 0) return;
  
  try {
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Sending Web Vitals:', metrics);
      return;
    }

    // In production, send to your analytics endpoint
    // Example: Supabase edge function or custom analytics service
    const response = await fetch('/api/analytics/web-vitals', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ metrics }),
      // Use keepalive to ensure delivery even on page unload
      keepalive: true,
    });

    if (!response.ok) {
      console.warn('[Analytics] Failed to send metrics:', response.status);
    }
  } catch (error) {
    // Silently fail - don't block user experience
    console.warn('[Analytics] Error sending metrics:', error);
  }
};

// Flush the queue
const flushQueue = () => {
  if (metricsQueue.length === 0) return;
  
  const metricsToSend = [...metricsQueue];
  metricsQueue = [];
  
  sendToAnalytics(metricsToSend);
};

// Schedule flush
const scheduleFlush = () => {
  if (flushTimeout) return;
  
  flushTimeout = setTimeout(() => {
    flushQueue();
    flushTimeout = null;
  }, FLUSH_INTERVAL);
};

// Report a single metric
export const reportWebVital = (metric: WebVitalMetric) => {
  const payload: AnalyticsPayload = {
    metric,
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    connection: getConnectionInfo(),
    deviceMemory: getDeviceMemory(),
  };

  metricsQueue.push(payload);

  // Flush immediately if queue is full
  if (metricsQueue.length >= MAX_QUEUE_SIZE) {
    flushQueue();
  } else {
    scheduleFlush();
  }
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushQueue();
    }
  });

  window.addEventListener('pagehide', () => {
    flushQueue();
  });
}

// Export analytics configuration
export const analyticsConfig = {
  enabled: process.env.NODE_ENV === 'production',
  sampleRate: 1.0, // 100% of users
  debug: process.env.NODE_ENV === 'development',
};

// Check if should report (for sampling)
export const shouldReportMetric = (): boolean => {
  if (!analyticsConfig.enabled) return analyticsConfig.debug;
  return Math.random() < analyticsConfig.sampleRate;
};
